import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";

import { QUESTIONS_IDS } from "@/constants/questionsIds";
import { StrapiQuestionsGroup } from "@/types/strapi";

import { useIsExperimentEnabledFunction } from "../ExperimentsProvider";
import { useGender } from "../GenderProvider";
import { getStrapiQuestionsGroups } from "../services/api";

import { getExperimentsIdsFromStrapiQuestions, getFormattedQuestions } from "./utils";

interface StrapiQuestionsType {
    experimentsIds?: string[];
    questions?: StrapiQuestionsGroup[];
    setQuestions?: (questions?: StrapiQuestionsGroup[]) => void;
}
const StrapiQuestionsContext = createContext<StrapiQuestionsType>({});

const StrapiQuestionsProvider = ({ children }: PropsWithChildren) => {
    const [questions, setQuestions] = useState<StrapiQuestionsGroup[]>();
    const [experimentsIds, setExperimentsIds] = useState<string[]>();

    useEffect(() => {
        if (questions) {
            setExperimentsIds(getExperimentsIdsFromStrapiQuestions(questions));
        }
    }, [questions]);

    return (
        <StrapiQuestionsContext.Provider value={{ experimentsIds, questions, setQuestions }}>
            {children}
        </StrapiQuestionsContext.Provider>
    );
};

interface strapiHookProps {
    questionId?: string;
}
export const useGetStrapiData = ({ questionId }: strapiHookProps) => {
    const { isFemale } = useGender();
    const { setQuestions } = useContext(StrapiQuestionsContext);

    const getQuestions = async () => {
        const questions = await getStrapiQuestionsGroups();
        setQuestions?.(questions);
    };

    useEffect(() => {
        const femaleQuestionToRequest = isFemale && questionId === QUESTIONS_IDS.DIET_TYPE;
        const maleQuestionToRequest = !isFemale && questionId === QUESTIONS_IDS.SQUATS;

        if (femaleQuestionToRequest || maleQuestionToRequest) {
            getQuestions();
        }
        // eslint-disable-next-line
    }, [questionId, isFemale]);
};

export const useStrapiExperimentsIds = () => {
    const { experimentsIds } = useContext(StrapiQuestionsContext);

    return experimentsIds;
};

export const useStrapiQuestions = ({ questionId }: strapiHookProps) => {
    useGetStrapiData({ questionId });

    const { getIsExperimentEnabled } = useIsExperimentEnabledFunction();
    const { prefix } = useGender();
    const { questions, experimentsIds } = useContext(StrapiQuestionsContext);

    const activeQuestionsGroup = useMemo(() => {
        const activeExpId = experimentsIds?.find((experimentId) =>
            getIsExperimentEnabled(experimentId)
        );
        if (activeExpId) {
            return questions?.find(({ eppo_key }) => eppo_key === activeExpId);
        }
        return null;
        // eslint-disable-next-line
    }, [experimentsIds, questions, questionId]);

    const formattedQuestions = useMemo(
        () => (activeQuestionsGroup ? getFormattedQuestions(activeQuestionsGroup, prefix) : null),
        [activeQuestionsGroup, prefix]
    );

    return { questions: formattedQuestions };
};

export default StrapiQuestionsProvider;
